// Jotai atoms backing the realtime event/chat namespaces declared in
// `plan-overlay-variables.md` §3.6. Hydrated by `useEventBusBridge`,
// consumed by `useLastEventVars` / `useChatVars` / `useGoalVars`.
//
// These are intentionally write-from-bridge / read-from-hooks: nothing
// outside the bridge should set them. The reactive shape is what the
// orchestrator merges into the resolver source tree.

import { atom } from "jotai";
import type { StreamEvent, ChatMessage } from "../services/eventBus/types";

/** Last-seen `kind: "follow"` event. `null` until the first one arrives. */
export const atomLastFollow = atom<StreamEvent | null>(null);

/** Last-seen `kind: "sub"` event (gift or otherwise). */
export const atomLastSub = atom<StreamEvent | null>(null);

/** Last-seen `kind: "donation"` event. */
export const atomLastDonation = atom<StreamEvent | null>(null);

/** Last-seen `kind: "raid"` event. */
export const atomLastRaid = atom<StreamEvent | null>(null);

/**
 * Rolling buffer of the last N chat messages (cap mirrors the service —
 * 200 by default). Newest at the end. Trimmed in-place by the bridge so
 * memory stays bounded across long streams.
 */
export const atomChatBuffer = atom<ChatMessage[]>([]);

/**
 * Whether the events SSE stream is currently connected. Useful to gate
 * UI affordances (e.g. show "EventBus offline" chip in the panel).
 * Driven by the events stream only — the chat stream connection state
 * is independent and not currently surfaced.
 */
export const atomEventBusActive = atom<boolean>(false);

/** Convenience selector — most recent chat message or `null`. */
export const atomChatLast = atom((get) => {
  const buffer = get(atomChatBuffer);
  return buffer.length > 0 ? buffer[buffer.length - 1] : null;
});
